import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Users, Save, Loader2, Info, Target, Award } from 'lucide-react';

const Phase7RolePlaySettings = () => {
    const { api, user } = useAuth();
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Only show for admin/trainer
    const isAdmin = user?.role === 'admin' || user?.role === 'trainer';

    useEffect(() => {
        if (isAdmin) {
            fetchSettings();
        }
    }, [isAdmin]);

    const fetchSettings = async () => {
        try {
            const response = await api.get('/rpat/certification/admin/phase7-settings');
            setSettings(response.data);
        } catch (error) {
            console.error('Failed to load Phase 7 settings:', error);
            // Set defaults
            setSettings({
                min_score_per_scenario: 75,
                min_average_score: 75,
                scenarios_per_category: 2,
                total_categories: 6,
                total_required: 12,
                score_range: { min: 50, max: 100 }
            });
        } finally {
            setLoading(false);
        }
    };

    const saveSettings = async () => {
        setSaving(true);
        try {
            const params = new URLSearchParams({
                min_score_per_scenario: settings.min_score_per_scenario,
                min_average_score: settings.min_average_score,
                scenarios_per_category: settings.scenarios_per_category
            });
            await api.put(`/rpat/certification/admin/phase7-settings?${params.toString()}`);
            toast.success('Phase 7 Role Play settings saved successfully!');
        } catch (error) {
            console.error('Failed to save settings:', error);
            toast.error(error.response?.data?.detail || 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    if (!isAdmin) {
        return null;
    }

    if (loading) {
        return (
            <Card>
                <CardContent className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </CardContent>
            </Card>
        );
    }

    const totalRequired = settings?.total_categories * settings?.scenarios_per_category;

    return (
        <Card data-testid="phase7-settings-card">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-emerald-500" />
                    R-PAT Phase 7 Role Play Settings
                </CardTitle>
                <CardDescription>
                    Control the score thresholds and requirements for Phase 7 Role Play Lab
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Info Banner */}
                <div className="flex items-start gap-3 p-4 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg border border-emerald-200 dark:border-emerald-800">
                    <Info className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-emerald-800 dark:text-emerald-200">
                        <p className="font-medium mb-1">Phase 7 Requirements</p>
                        <p>
                            Users must complete <strong>{settings?.scenarios_per_category} scenarios</strong> from each of the <strong>6 categories</strong> ({totalRequired} total).
                            Each scenario must score at least <strong>{settings?.min_score_per_scenario}%</strong>, 
                            and the overall average must be <strong>{settings?.min_average_score}%</strong> or higher.
                        </p>
                    </div>
                </div>

                {/* Min Score Per Scenario */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="space-y-1">
                            <Label className="flex items-center gap-2">
                                <Target className="h-4 w-4 text-emerald-500" />
                                Minimum Score Per Scenario
                            </Label>
                            <p className="text-xs text-muted-foreground">
                                Each completed scenario must meet this threshold
                            </p>
                        </div>
                        <Badge variant="outline" className="text-lg font-mono px-3 py-1">
                            {settings?.min_score_per_scenario}%
                        </Badge>
                    </div>
                    <Slider
                        value={[settings?.min_score_per_scenario || 75]}
                        onValueChange={([value]) => setSettings({ ...settings, min_score_per_scenario: value })}
                        min={50}
                        max={100}
                        step={5}
                        className="w-full"
                        data-testid="min-score-slider"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                        <span>50% (Easier)</span>
                        <span>100% (Harder)</span>
                    </div>
                </div>

                {/* Min Average Score */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="space-y-1">
                            <Label className="flex items-center gap-2">
                                <Award className="h-4 w-4 text-amber-500" />
                                Minimum Average Score
                            </Label>
                            <p className="text-xs text-muted-foreground">
                                Overall average across all {totalRequired} scenarios
                            </p>
                        </div>
                        <Badge variant="outline" className="text-lg font-mono px-3 py-1">
                            {settings?.min_average_score}%
                        </Badge>
                    </div>
                    <Slider
                        value={[settings?.min_average_score || 75]}
                        onValueChange={([value]) => setSettings({ ...settings, min_average_score: value })}
                        min={50}
                        max={100}
                        step={5}
                        className="w-full"
                        data-testid="avg-score-slider"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                        <span>50% (Easier)</span>
                        <span>100% (Harder)</span>
                    </div>
                </div>

                {/* Scenarios Per Category */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="space-y-1">
                            <Label className="flex items-center gap-2">
                                <Users className="h-4 w-4 text-blue-500" />
                                Scenarios Per Category
                            </Label>
                            <p className="text-xs text-muted-foreground">
                                How many scenarios users must complete in each category
                            </p>
                        </div>
                        <Badge variant="outline" className="text-lg font-mono px-3 py-1">
                            {settings?.scenarios_per_category}
                        </Badge>
                    </div>
                    <Slider
                        value={[settings?.scenarios_per_category || 2]}
                        onValueChange={([value]) => setSettings({ ...settings, scenarios_per_category: value })}
                        min={1}
                        max={4}
                        step={1}
                        className="w-full"
                        data-testid="scenarios-per-category-slider"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                        <span>1 per category ({settings?.total_categories} total)</span>
                        <span>4 per category ({settings?.total_categories * 4} total)</span>
                    </div>
                </div>

                {/* Current Requirements Summary */}
                <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm font-medium mb-2">Current Requirements Summary</p>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                        <div className="flex items-center gap-2">
                            <Target className="h-4 w-4 text-emerald-500" />
                            <span>Min: <strong>{settings?.min_score_per_scenario}%</strong> each</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Award className="h-4 w-4 text-amber-500" />
                            <span>Avg: <strong>{settings?.min_average_score}%</strong></span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-blue-500" />
                            <span>Total: <strong>{totalRequired} scenarios</strong></span>
                        </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                        6 categories × {settings?.scenarios_per_category} scenarios = {totalRequired} total scenarios required
                    </p>
                </div>

                {/* Save Button */}
                <Button
                    onClick={saveSettings}
                    disabled={saving}
                    className="w-full"
                    data-testid="save-phase7-settings-btn"
                >
                    {saving ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Saving...
                        </>
                    ) : (
                        <>
                            <Save className="mr-2 h-4 w-4" />
                            Save Phase 7 Settings
                        </>
                    )}
                </Button>
            </CardContent>
        </Card>
    );
};

export default Phase7RolePlaySettings;
